import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import rgba from 'polished/lib/color/rgba';
import dayjs from 'dayjs';
import { palette } from 'theme';
import { Link } from 'react-router-dom';
import { withTheme } from '@material-ui/core/styles';

import Typography from '@material-ui/core/Typography';

import CircleIcon from 'mdi-material-ui/Circle';

import FeaturedCard from '../elements/FeaturedCard';

import formatPublicationDetails from '../utilities/formatPublicationDetails';

const StyledFeaturedCard = styled(FeaturedCard)`
  width: 100%;
  display: block;
  text-decoration: none;
  margin-bottom: 0;
  display: flex;
  > div {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  &:hover {
    box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.15);
  }
`;

const MetaTypography = styled(Typography)`
  margin: 6px ${props => (props.padRight ? '80px' : 0)} 24px 0;
`;

const ExcerptTypography = styled(Typography)`
  max-height: 200px;
  overflow: hidden;
  position: relative;
  &:after {
    transition: all 1s linear;
    background: linear-gradient(
      ${props => rgba(props.background || '#ffffff', 0)} 30%,
      ${props => rgba(props.background || '#ffffff', props.in ? 0 : 1)}
    );
    position: absolute;
    z-index: 1;
    display: block;
    width: 100%;
    height: 80px;
    content: '';
    bottom: 0;
  }
`;

const ResultFacets = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 100%;
`;

const ProductInfo = styled(Typography)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin: 0;
  min-height: 24px;
  max-width: 100%;
  :not(:last-of-type) {
    margin-right: 16px;
  }
  img {
    width: 24px;
    height: 24px;
    margin-right: 6px;
  }
`;

const CatTagInfo = styled(({ catTag, ...other }) => (
  <span {...other}>
    <CircleIcon />
    <Typography variant="subtitle2" noWrap>
      {catTag.map((item, idx) => item.title).join(', ')}
    </Typography>
  </span>
))`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  max-width: 100%;

  svg {
    fill: ${palette.grey[100]};
    width: 8px;
    height: 8px;
    margin: 0 10px;
  }
`;

const PublicationCard = ({ publication, theme, showBackground, ...other }) => {
  const pubDetails = formatPublicationDetails(publication);
  const backgroundColor =
    theme.palette.type === 'light' && showBackground
      ? theme.palette.grey[50]
      : theme.palette.background.paper;

  return pubDetails ? (
    <StyledFeaturedCard
      component={Link}
      to={publication.url}
      featured={publication.is_featured}
      background={backgroundColor}
      {...other}
    >
      <MetaTypography
        variant="overline"
        gutterBottom
        padRight={publication.is_featured}
      >
        {pubDetails.journal},{' '}
        {dayjs(pubDetails.published_at).format('MMM YYYY')}
      </MetaTypography>
      <Typography variant="h3" gutterBottom>
        {publication.title}
      </Typography>
      <ExcerptTypography
        variant="body2"
        component="div"
        background={backgroundColor}
        gutterBottom
      >
        <p>{pubDetails.authors[0]} et al.</p>
        {publication.is_featured ? (
          publication.featured_content && (
            <em
              dangerouslySetInnerHTML={{
                __html: _.get(publication, 'featured_content[0].rich_text.text')
              }}
            />
          )
        ) : (
          <em
            dangerouslySetInnerHTML={{
              __html: pubDetails.abstract
            }}
          />
        )}
      </ExcerptTypography>
      <ResultFacets>
        {publication.categories_tags.product &&
          publication.categories_tags.product.map((productItem, idx) => (
            <ProductInfo variant="subtitle2" key={idx} noWrap>
              <img
                src={_.get(productItem.icon, 'url')}
                alt={productItem.title}
              />{' '}
              {productItem.title}{' '}
            </ProductInfo>
          ))}
        {publication.categories_tags.application && (
          <CatTagInfo catTag={publication.categories_tags.application} />
        )}
        {publication.categories_tags.research_area && (
          <CatTagInfo catTag={publication.categories_tags.research_area} />
        )}
      </ResultFacets>
    </StyledFeaturedCard>
  ) : null;
};

export default withTheme()(PublicationCard);
